import { isReadSubject, subjectItem } from "./read-subjects";
import {
  type AnswerMap,
  type Condition,
  type ConditionOp,
  type FormDefinition,
  type FormItem,
  type FormSection,
  type ItemType,
  type ReadValues,
} from "./types";

const DATE_RE = /^\d{4}-\d{2}-\d{2}$/;
const READ_NUMBER_RE = /^-?\d+(\.\d+)?$/;
// The RFC3339 shape the Go twin's datetimeMilli requires. Date.parse alone also
// admits space-separated and offset-less local forms Go refuses, and a read
// string must coerce identically in both twins.
const RFC3339_RE = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(\.\d+)?(Z|[+-]\d{2}:\d{2})$/;

export interface ItemIndex {
  ordered: FormItem[];
  byId: Map<string, FormItem>;
}

export function flattenItems(def: FormDefinition): ItemIndex {
  const ordered: FormItem[] = [];
  const byId = new Map<string, FormItem>();
  const walk = (section: FormSection): void => {
    if (section.repeats === true) return; // repeating rows are evaluated via rowEnabledSet, not the flat index
    for (const item of section.items) {
      ordered.push(item);
      byId.set(item.link_id, item);
    }
    if (section.sections) for (const sub of section.sections) walk(sub);
  };
  for (const section of def.sections) walk(section);
  return { ordered, byId };
}

/**
 * Every repeating section in document order, including one level of
 * repeating sub-sections nested under a non-repeating parent.
 */
export function repeatingSections(def: FormDefinition): FormSection[] {
  const out: FormSection[] = [];
  for (const s of def.sections) {
    if (s.repeats === true) out.push(s);
    for (const sub of s.sections ?? []) if (sub.repeats === true) out.push(sub);
  }
  return out;
}

/**
 * Evaluates one repeating section's items against a single row's answers.
 * Delegates to `enabledSet` over a synthetic one-section definition — zero
 * new condition logic; the absent-answer truth table applies to row answers
 * unchanged. Conditions may reference same-row siblings only (the section's
 * children are excluded from the outer `flattenItems` index). The synthetic
 * section drops the section's own `enable_when`, which `enabledSet` evaluates
 * at the section's document position, and no reads flow in: a row child is
 * never display-bound.
 */
export function rowEnabledSet(section: FormSection, rowAnswers: AnswerMap): Map<string, boolean> {
  const synthetic: FormDefinition = {
    schema_version: 2,
    target: "premise",
    sections: [{ link_id: section.link_id, items: section.items }],
  };
  return enabledSet(synthetic, rowAnswers);
}

// Exported for the read-values sibling module; not on the barrel.
export function valueTypeValid(item: FormItem, raw: unknown): boolean {
  switch (item.type) {
    case "pass_fail":
      return raw === "pass" || raw === "fail";
    case "boolean":
      return typeof raw === "boolean";
    case "number":
      return typeof raw === "number" && Number.isFinite(raw);
    case "text":
    case "select_one":
      return typeof raw === "string";
    case "select_multiple":
      return Array.isArray(raw) && raw.every((v) => typeof v === "string");
    case "date":
      return typeof raw === "string" && DATE_RE.test(raw);
    case "datetime":
      return typeof raw === "string" && !Number.isNaN(Date.parse(raw));
    default:
      return false; // document / signature / instruction never answer logic
  }
}

/**
 * A display-mode read-source item: the value is server-resolved and rendered
 * read-only. Exported for the package's sibling modules; not on the barrel.
 */
export function isDisplayReadBound(
  item: FormItem,
): item is FormItem & { read_source: string; read_mode: "display" } {
  return item.read_source !== undefined && item.read_mode === "display";
}

/**
 * The one read-string coercion, identical in both twins: the typed value a
 * resolved read string carries for the item's type, `undefined` (absent) on
 * any miss — a missing read included. A `select_one` read is its option
 * string, compared through the `option` operand.
 */
export function readValue(
  type: ItemType,
  s: string | undefined,
): string | number | boolean | undefined {
  if (s === undefined) return undefined;
  switch (type) {
    case "text":
    case "select_one":
      return s !== "" ? s : undefined;
    case "number": {
      if (!READ_NUMBER_RE.test(s)) return undefined;
      const n = Number(s);
      return Number.isFinite(n) ? n : undefined;
    }
    case "boolean":
      return s === "true" ? true : s === "false" ? false : undefined;
    case "date":
      return DATE_RE.test(s) ? s : undefined;
    case "datetime":
      return RFC3339_RE.test(s) && !Number.isNaN(Date.parse(s)) ? s : undefined;
    default:
      return undefined; // the read-source registries and the custom-field type map admit no other type
  }
}

// Exported for the computed-fields sibling module (same package); not
// part of the package barrel — presence semantics stay single-sourced.
// `answerPresent` below is the barrel's one presence export and wraps this.
export function presentAnswer(
  item: FormItem,
  answers: AnswerMap,
  enabled: ReadonlyMap<string, boolean>,
  reads?: ReadValues,
): { raw: unknown; present: boolean } {
  if (enabled.has(item.link_id) && enabled.get(item.link_id) === false) {
    return { raw: undefined, present: false }; // cascade masking
  }
  if (isDisplayReadBound(item)) {
    // The stored answer of a display item is only the seal of its read;
    // logic reads the value itself.
    const value = readValue(item.type, reads?.[item.read_source]);
    return { raw: value, present: value !== undefined };
  }
  const raw = answers[item.link_id];
  return { raw, present: storedAnswerPresent(item, raw) };
}

// Whether a stored value is an answer: set, type-valid and not an empty
// marker. Exported for the validator sibling, which checks a display item's
// sealed value this way; not part of the package barrel.
export function storedAnswerPresent(item: FormItem, raw: unknown): boolean {
  if (raw === undefined || raw === null) return false;
  if (!valueTypeValid(item, raw)) return false;
  if ((item.type === "text" || item.type === "select_one") && raw === "") return false;
  if (item.type === "select_multiple" && (raw as string[]).length === 0) return false;
  return true;
}

/** The non-empty client-minted attachment ids in a document answer. */
function attachmentIdList(raw: unknown): string[] {
  if (!Array.isArray(raw)) return [];
  return raw.filter((v): v is string => typeof v === "string" && v !== "");
}

/**
 * THE ONE PRESENCE RULE: does this item carry an answer?
 *
 * It backs `N of M answered` on both platforms — the shared `sectionProgress`
 * and the mobile `requiredProgress` / finalize gate — so a section
 * header, a progress rail and a jump sheet describing the same document print
 * the same numbers. Two implementations that agree today have no mechanism to
 * stay equal, and the pair that existed did not: web read a value-shape test
 * (undefined/null/""/[] absent, everything else present) while mobile delegated
 * to the evaluator's `exists`, whose type table returns false for the
 * ATTACHMENT types. An attachment item was therefore counted in `M` and could
 * never be counted in `N` on mobile — such a section could not read `N of N`
 * — while the same document read it as answered on web.
 *
 * The two attachment types are checked STRUCTURALLY rather than through
 * `presentAnswer`: their answers are client-minted attachment ids, they are
 * publish-gated out of logic entirely (never a condition question, a copy
 * source or a read binding), and the evaluator's type table is about what can
 * answer LOGIC. Everything else delegates, so the absent-answer
 * truth table (missing key, null, "" for text/select_one, [] for
 * select_multiple, a type-invalid value, disabled cascade masking) keeps
 * exactly one implementation.
 *
 * This matches the server, which is the arbiter: `answerShapeValid` +
 * `answerNonEmpty` in apps/api/internal/compliance/formsubmission/validate.go
 * read a non-empty string array as a present `document` answer and a
 * non-empty string as a present `signature`.
 */
export function answerPresent(
  item: FormItem,
  answers: AnswerMap,
  enabled: ReadonlyMap<string, boolean>,
): boolean {
  if (item.type === "document") {
    return attachmentIdList(answers[item.link_id]).length > 0;
  }
  if (item.type === "signature") {
    const raw = answers[item.link_id];
    return typeof raw === "string" && raw !== "";
  }
  return presentAnswer(item, answers, enabled).present;
}

function operandFor(item: FormItem, c: Condition): unknown {
  const a = c.answer;
  if (a === undefined) return undefined;
  switch (item.type) {
    case "pass_fail":
      return a.pass_fail;
    case "boolean":
      return a.boolean;
    case "number":
      return a.number;
    case "text":
      return a.text;
    case "select_one":
    case "select_multiple":
      return a.option;
    case "date":
      return a.date;
    case "datetime":
      return a.datetime;
    default:
      return undefined;
  }
}

// The `as` casts below are safe: presentAnswer's valueTypeValid check already
// proved `raw` matches the question type, and a present operand was extracted
// from the matching TypedAnswer member.
function typedEquals(item: FormItem, raw: unknown, operand: unknown): boolean {
  if (item.type === "select_multiple") return (raw as string[]).includes(operand as string);
  if (item.type === "datetime") return Date.parse(raw as string) === Date.parse(operand as string);
  return raw === operand;
}

function orderedCompare(item: FormItem, raw: unknown, operand: unknown, op: ConditionOp): boolean {
  let a: number | string;
  let b: number | string;
  if (item.type === "number") {
    a = raw as number;
    b = operand as number;
  } else if (item.type === "date") {
    // ISO dates order lexicographically = chronologically
    a = raw as string;
    b = operand as string;
  } else if (item.type === "datetime") {
    // compare instants in epoch milliseconds so offsets normalize
    a = Date.parse(raw as string);
    b = Date.parse(operand as string);
  } else {
    return false; // ordering undefined for this type (publish-gated)
  }
  if (op === "gt") return a > b;
  if (op === "ge") return a >= b;
  if (op === "lt") return a < b;
  if (op === "le") return a <= b;
  return false;
}

export function evalCondition(
  c: Condition,
  idx: ItemIndex,
  answers: AnswerMap,
  enabled: Map<string, boolean>,
  reads?: ReadValues,
): boolean {
  // A question naming no item but a read subject evaluates the subject's
  // synthetic display item; any other unknown question is publish-gated and
  // false for defensive totality.
  const q = idx.byId.get(c.question) ?? (isReadSubject(c.question) ? subjectItem(c) : undefined);
  if (!q) return false;
  const { raw, present } = presentAnswer(q, answers, enabled, reads);
  if (c.op === "exists") return present;
  if (!present) return false; // absent-answer truth table: non-exists → false
  const operand = operandFor(q, c);
  // Operand/question type mismatch is false for EVERY operator — including ne,
  // where negating typedEquals would otherwise turn the mismatch into true.
  if (operand === undefined) return false;
  if (c.op === "eq") return typedEquals(q, raw, operand);
  if (c.op === "ne") return !typedEquals(q, raw, operand);
  return orderedCompare(q, raw, operand, c.op);
}

/**
 * The shared condition-list combiner: an all-mode AND / any-mode OR fold over
 * `evalCondition`. The empty-list → true short-circuit lives INSIDE so every
 * caller shares one vacuous-truth rule — hoisting it out would flip
 * `enabledSet`'s any-mode-empty semantics (an item or section with
 * `enable_behavior: "any"` and no conditions must be enabled). Shared by the
 * section and item `enable_when` gating and by the client composers'
 * `copy_from` gate — the web composer and the mobile hook evaluate
 * `copy_from.when` through this exact function, never a re-rolled condition
 * loop.
 */
export function evaluateConditions(
  conditions: Condition[] | undefined,
  anyMode: boolean,
  idx: ItemIndex,
  answers: AnswerMap,
  enabled: Map<string, boolean>,
  reads?: ReadValues,
): boolean {
  const conds = conditions ?? [];
  if (conds.length === 0) return true;
  for (const c of conds) {
    const ok = evalCondition(c, idx, answers, enabled, reads);
    if (anyMode && ok) return true;
    if (!anyMode && !ok) return false;
  }
  return !anyMode;
}

function conditionsSatisfied(
  item: FormItem,
  idx: ItemIndex,
  answers: AnswerMap,
  enabled: Map<string, boolean>,
  reads?: ReadValues,
): boolean {
  return evaluateConditions(
    item.enable_when,
    item.enable_behavior === "any",
    idx,
    answers,
    enabled,
    reads,
  );
}

/**
 * Single forward pass in document order (forward refs are publish-gated),
 * masking disabled ancestors as absent. Sections and items share one link_id
 * namespace, so the map carries both: a section is enabled only under an
 * enabled parent and by its own conditions, and an item only inside an enabled
 * section. A repeating section gets its own entry; its rows are
 * `rowEnabledSet`'s.
 *
 * PURE, and it stays that way. This sweep is how web, mobile, company and
 * customer all gate a regulatory document, and it must be a function of its
 * arguments alone — never of call history.
 *
 * A module-level slot keyed on argument reference identity used to live here,
 * to spare a mobile execution screen the repeated sweep. It was removed
 * because reference identity does not track an AnswerMap's CONTENTS, and
 * nothing on any client promises those are immutable: `finalizeAnswers` seals
 * the computed values into its `responses` object between its
 * `computedFieldValues` and `computedOutcome` calls and hands that same object
 * to both, so the outcome was being decided against the enabled map of the
 * document as it stood before the seal. The outcome evaluator overlays the
 * computed values onto its own copy of the answers, so the verdict does not
 * depend on that seal order — but a cache keyed on a mutated object would
 * still hand any caller the enabled map of a document that no longer exists,
 * and an offline client's correctness must not rest on every caller's call
 * order.
 *
 * The cache also bought less than it looked like: `rowEnabledSet` mints a fresh
 * synthetic definition per call, so every row of every repeating collection
 * missed the single slot and evicted it. A caller that wants to pay for one
 * sweep instead of several hoists the result and passes it down — which is what
 * `sectionProgress` and `requiredProgress` both take as an argument.
 */
export function enabledSet(
  def: FormDefinition,
  answers: AnswerMap,
  reads?: ReadValues,
): Map<string, boolean> {
  const idx = flattenItems(def);
  const enabled = new Map<string, boolean>();
  const walk = (section: FormSection, parentEnabled: boolean): void => {
    const own =
      parentEnabled &&
      evaluateConditions(
        section.enable_when,
        section.enable_behavior === "any",
        idx,
        answers,
        enabled,
        reads,
      );
    enabled.set(section.link_id, own);
    if (section.repeats === true) return;
    for (const item of section.items) {
      enabled.set(item.link_id, own && conditionsSatisfied(item, idx, answers, enabled, reads));
    }
    for (const sub of section.sections ?? []) walk(sub, own);
  };
  for (const section of def.sections) walk(section, true);
  return enabled;
}
